import CloseIcon from '@mui/icons-material/Close'
import {
  Card,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  Link,
} from '@mui/material'
import { resetCache as resetCsvConfigCache } from 'api/csv'
import { delOcrBuilder } from 'api/ocrBuildersConfig'
import AddOcrBuilderDialog from 'components/AppDialogs/AddOcrBuilderDialog'
import { useAlert, useConfirm } from 'hooks/useDialog'
import { useState, useCallback } from 'react'
import { useRevalidator } from 'react-router-dom'
import { timeFormat } from 'utils/format'

import { useOcrBuilderConfigLoader } from './OcrBuilderConfig.loader'
import TabContent from './TabContent'

const CANNOT_DELETE_OCR_BUILDER_CODE = 'CANNOT_DELETE_OCR_BUILDER'

export default function OcrBuilderConfig() {
  const revalidator = useRevalidator()
  const confirm = useConfirm()
  const alert = useAlert()
  const ocrBuildersConfig = useOcrBuilderConfigLoader()
  const [ocrBuilderConfigID, setOcrBuilderConfigID] = useState(-1)
  const readerHelpIconProps = {
    builderPath: '/builder/ja-JP/ocr-reader-doc/connect-to-ocr-builder.html#OcrBuilder',
    concurPath: '',
    invoicePath: '',
    orderFormPath: '',
  }

  const del = useCallback(
    (ocrBuilderConfigID: number) => {
      const onConfirm = async () => {
        resetCsvConfigCache()
        try {
          const r = await delOcrBuilder(ocrBuilderConfigID)
          if (r.status === 'NG') {
            throw new Error(CANNOT_DELETE_OCR_BUILDER_CODE)
          }
          revalidator.revalidate()
          // TODO: status !== 'OK'???
          alert({ message: '削除しました' })
        } catch (error: any) {
          if (error.message === CANNOT_DELETE_OCR_BUILDER_CODE) {
            alert({ message: 'この文書タイプで登録済みの文書が存在するため、削除できません' })
          } else {
            alert({ message: '削除に失敗しました' })
          }
        }
      }

      confirm({
        message: '登録済みの文書タイプを削除しますか',
        onConfirm,
      })
    },
    [alert, confirm, revalidator],
  )

  const ocrBuilderRows = (ocrBuildersConfig || []).map((item) => (
    <TableRow key={item.ocrBuilderConfigID}>
      <TableCell
        sx={{
          width: '50%',
          wordBreak: 'break-all',
        }}
      >
        <Link
          align="left"
          underline="always"
          component="button"
          onClick={() => setOcrBuilderConfigID(item.ocrBuilderConfigID)}
        >
          {item.documentTypeName}
        </Link>
      </TableCell>
      <TableCell>{item.subGroup || '共通文書タイプ'}</TableCell>
      <TableCell>{item.createdAt && timeFormat(item.createdAt)}</TableCell>
      <TableCell>
        <IconButton size="small" aria-label="delete" onClick={() => del(item.ocrBuilderConfigID)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  ))

  return (
    <>
      <TabContent
        title="Template OCR連携設定"
        readerHelpIconProps={readerHelpIconProps}
        description="Template OCRで作成されたOCRドメインと接続します。登録された文書タイプは、選択された範囲のユーザー（所属グループもしくはすべてのユーザー）で利用可能となります。"
      >
        <h3>新規登録</h3>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <p>新しい文書タイプの登録</p>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => setOcrBuilderConfigID(0)}
            data-testid="register-button"
          >
            登録
          </Button>
        </Box>
        <h3>登録済みの文書タイプ一覧</h3>
        <Card variant="outlined">
          <TableContainer>
            <Table>
              <TableHead data-testid="table-head-ocrbuilder">
                <TableRow>
                  <TableCell>文書タイプ名</TableCell>
                  <TableCell>グループ</TableCell>
                  <TableCell>登録日時</TableCell>
                  <TableCell>削除</TableCell>
                </TableRow>
              </TableHead>
              <TableBody data-testid="table-body-ocrbuilder">{ocrBuilderRows}</TableBody>
            </Table>
          </TableContainer>
        </Card>
      </TabContent>
      <AddOcrBuilderDialog
        ocrBuilderConfigID={ocrBuilderConfigID}
        setOcrBuilderConfigID={setOcrBuilderConfigID}
        ocrBuilderData={
          ocrBuilderConfigID > 0
            ? ocrBuildersConfig.find((item) => item.ocrBuilderConfigID === ocrBuilderConfigID)
            : undefined
        }
      />
    </>
  )
}
